import { Injectable } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SharedService } from 'src/app/modules/shared/_services/shared.service';
import { TenderService } from 'src/app/modules/tenders/_services/tender.service';
import { PARAMS } from 'src/app/_configs/params';

@Injectable({
  providedIn: 'root'
})
export class TenderDetailsResolver implements Resolve<any> {

  private readonly TENDER_DETAILS_PAGE = makeStateKey<object>(PARAMS.STATIC_PARAMS.TENDER_DETAILS_PAGE);
  private readonly TOP_TWENTY_LISTING_CITIES = makeStateKey<object>(PARAMS.STATIC_PARAMS.TENDER_TWENTY_CITIES_PAGE);

  constructor(
    private tenderService: TenderService,
    private transferState: TransferState,
    private sharedService: SharedService
  ) { }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    // Check if data is already in TransferState
    if (this.transferState.hasKey(this.TENDER_DETAILS_PAGE) && this.transferState.hasKey(this.TOP_TWENTY_LISTING_CITIES)) {
      const tenderDetails = this.transferState.get(this.TENDER_DETAILS_PAGE, {});
      const tenderByCities = this.transferState.get(this.TOP_TWENTY_LISTING_CITIES, {});

      // Remove from TransferState to prevent stale data
      this.transferState.remove(this.TENDER_DETAILS_PAGE);
      this.transferState.remove(this.TOP_TWENTY_LISTING_CITIES);

      return of([tenderDetails, tenderByCities]);
    }

    // Fetch data if not available in TransferState
    const stateId = route.params.stateId;
    const authorityId = route.params.authorityId;

    const tenderId = route.params.tenderId;

    // API calls with error handling
    const tenderDetails$ = this.tenderService.retrieveTenderDetails(tenderId, stateId, authorityId).pipe(
      catchError(error => {
        console.error('Error fetching tender details:', error);
        return of({});
      })
    );

    const tenderByCities$ = this.sharedService.retrieveTenderByCitiesNew().pipe(
      catchError(error => {
        console.error('Error fetching tender by cities:', error);
        return of({});
      })
    );

    // Use forkJoin to make parallel API calls
    return forkJoin([tenderDetails$, tenderByCities$]).pipe(
      map(([tenderDetails, tenderByCities]) => {
        // Cache data for SSR
        if (this.sharedService.isServer) {
          if (tenderDetails) {
            this.transferState.set(this.TENDER_DETAILS_PAGE, tenderDetails);
          }
          if (tenderByCities) {
            this.transferState.set(this.TOP_TWENTY_LISTING_CITIES, tenderByCities);
          }
        }
        return [tenderDetails, tenderByCities];
      })
    );
  }
}
